/**
 * A single system at game detail.
 *
 * The starmap deliberately shows a station as a bare footprint — enough to know
 * one exists. This is the other half of that split: real pads with their
 * markings, surface clutter, and planets at full shader quality. Nothing here
 * is scaled down, so distances are the ones flight will actually use.
 */

import {
  type HeatSource,
  PAD_LABELS,
  Rng,
  flightTime,
  getStationGeometry,
  getSystem,
  hashSeed,
  orbitPath,
  positionAt,
  stationVolumes,
  type Orbit,
  type SizeClass,
  type StationGeometry,
  type StationVolume,
  type SystemId,
  asteroidsIn,
  fieldSpin,
  fieldTint,
  MAX_ROCK_RADIUS,
  type Asteroid,
  type AsteroidField,
  type RingBasis,
} from '@interspacial/shared';
import * as THREE from 'three';

import { ALLEGIANCE_COLORS } from './starmapScene';
import { createPlanetMaterial } from './shaders/planet';
import { createSunGlowMaterial, createSunMaterial } from './shaders/sun';
import { createStructureMaterial, withEdges } from './shaders/structure';
import { loadComponentGeometry } from './models';

/**
 * World units per generator unit, in flight.
 *
 * The map and the game want different scales, and this is where they part.
 * The map is a chart: it wants everything small enough to sit side by side and
 * be compared. Flight is not a chart — you are *inside* it, and the sizes have
 * to be the sizes they would really be.
 *
 * The fixed reference is the ship, because a ship is the one thing not sized by
 * the generator: hulls come out of the line-and-joint editor at 18 to 90 units
 * whatever else happens. Everything else is measured against that, and at
 * generator scale the comparison was plainly broken — a massive hull is 90
 * units across and the massive berth it lands on was 10. A ship was nine times
 * wider than its own pad.
 *
 * At twenty, a massive pad is 200 units and takes a 90-unit hull with room to
 * spare, station decks run 90 to 700, and planets run 560 to 2400 — so a ship
 * is a speck against a world, small against a station, and comfortably at home
 * on a pad. Which is the ordering the game is meant to have.
 */
export const GAME_SCALE = 20;

/**
 * How near a body has to be for the detailed mesh, in world units of surface
 * distance.
 *
 * Sphere tessellation is the cheapest thing in this scene to get wrong in
 * either direction: at 64×48 a planet you are nowhere near costs thousands of
 * triangles for a shape that is three pixels across, and at 24×16 one you are
 * flying past has a visibly faceted horizon. So both exist and the renderer
 * picks — `THREE.LOD` swaps them itself during projection, no per-frame work
 * here.
 */
const DETAIL_RANGE = 1000;

/**
 * How long a body takes to turn once, in seconds of world time.
 *
 * Bodies were static, which is what made a system read as a diorama: orbits
 * moved, and every surface on every one of them was nailed in place. Big things
 * turn slowly and small things quickly — roughly what you would expect, and
 * enough variety that no two neighbours look synchronised.
 */
const SPIN_PERIOD = { min: 90, max: 420 } as const;

/**
 * A body, at two levels of detail.
 *
 * The material is shared between them — it is the same surface either way, and
 * a planet shader compiled twice is a pipeline stall for nothing.
 */
function detailedSphere(
  radius: number,
  material: THREE.Material,
  segments: { near: [number, number]; far: [number, number] },
): THREE.LOD {
  const lod = new THREE.LOD();

  lod.addLevel(new THREE.Mesh(new THREE.SphereGeometry(radius, ...segments.near), material), 0);
  lod.addLevel(
    new THREE.Mesh(new THREE.SphereGeometry(radius, ...segments.far), material),
    // `LOD` compares against a *world* distance between camera and object — it
    // does not undo the object's own scale — so the threshold has to be scaled
    // up out of the generator units everything here is built in. Measured from
    // the surface rather than the centre, since that is what "close" means when
    // the thing is two thousand units across.
    radius * GAME_SCALE + DETAIL_RANGE,
  );

  return lod;
}

/** What a marker is drawn around, which decides how it is drawn. */
export type MarkerKind = 'star' | 'planet' | 'moon' | 'station' | 'anomaly';

/**
 * Something the HUD draws a bracket around when it is near the centre of view.
 *
 * Replaces the floating name that used to hang over every object. Those were
 * unanchored — a name in space with nothing tying it to the thing it named —
 * and with a dozen bodies on screen they overlapped into a wall of text. A box
 * says exactly what it is attached to and how big that thing is on screen.
 */
export interface SystemSceneLabel {
  text: string;
  kind: MarkerKind;
  node: THREE.Object3D;
  /** World-space radius, so the bracket can be sized to the object. */
  radius: number;
}

/** Something the camera can lock onto and then ride along with. */
export interface Focusable {
  node: THREE.Object3D;
  label: string;
  /** Roughly how big the thing is, for choosing a sensible viewing distance. */
  radius: number;
  /**
   * What it is.
   *
   * Carried rather than guessed from the radius, which is what flight was doing
   * — and a guess that says "any body over 400 units is a planet" is one bad
   * generator roll away from mass-locking you to a station as if it were a
   * world.
   */
  kind: MarkerKind;
}

/**
 * A berth, located rather than drawn.
 *
 * Docking has to ask "am I over that plate" of every pad in the system every
 * frame, and the plate is a mesh buried inside an instanced deck. So the
 * position comes out here, deck-local, and the caller transforms it through the
 * live deck node — which is also what makes it keep working while the station
 * orbits.
 */
export interface Berth {
  stationId: string;
  stationName: string;
  padId: string;
  /** Its class, so an oversized berth can be charged for. */
  size: SizeClass;
  /** The deck it belongs to. Everything below is in this node's own frame. */
  deck: THREE.Object3D;
  /** Pad centre, on the deck's top face. */
  local: THREE.Vector3;
  /** Pad width. */
  extent: number;
}

/**
 * Something round enough to hit with a distance check.
 *
 * The star, the planets and their moons. Stations and anomalies are not in here
 * because they are not spheres — a deck is a flat prism and would need its own
 * test, which is the next piece of work rather than this one.
 *
 * The star *is*, and it is not a focusable, which is why this list exists
 * separately rather than being filtered out of that one: the one body you can
 * most obviously fly into was the one thing nothing tracked.
 */
export interface CollidableSphere {
  node: THREE.Object3D;
  label: string;
  /** World-space radius. */
  radius: number;
}

/**
 * A belt, as something to run into.
 *
 * Everything here was already computed to *draw* the field — the rock list, the
 * positions, the ring's axes. Keeping them costs nothing and means the collision
 * test measures against exactly what is on screen, rather than a second opinion
 * derived the same way twice.
 */
export interface CollidableField {
  field: AsteroidField;
  rocks: readonly Asteroid[];
  /** Rock centres in the field's frame: three floats each, in `rocks` order. */
  positions: Float32Array;
  basis: RingBasis;
  /** The turning frame the rocks live in. Converts a world point into theirs. */
  node: THREE.Object3D;
}

export interface SystemScene {
  group: THREE.Group;
  /** Furthest orbit, so the camera can frame the whole thing. */
  extent: number;
  /**
   * Each station's deck node, by station id.
   *
   * Docking needs to attach to a specific berth on a specific station and then
   * ride along as it orbits, which means reaching the live node rather than a
   * position captured at build time.
   */
  stationNodes: Map<string, THREE.Object3D>;
  /**
   * What a station is made of, for collision, in each deck's own frame.
   *
   * Built from the same geometry the meshes are drawn from, so a station cannot
   * look like one thing and collide like another. Deck-local because that frame
   * already carries the station's orbit, spin and scale — testing there is one
   * `worldToLocal` rather than transforming every volume every frame.
   */
  stationHulls: { deck: THREE.Object3D; radius: number; volumes: StationVolume[] }[];
  berths: Berth[];
  /**
   * Where the star is, as the shaders see it.
   *
   * Exposed because a view that renders with a floating origin has moved the
   * whole system out from under them: lighting is computed against the world
   * position the vertex shader derives from `modelMatrix`, so if that frame
   * shifts, the light has to shift with it or every planet is lit from the
   * wrong side. Mutate in place — the materials hold this exact vector.
   */
  lightPosition: THREE.Vector3;
  labels: SystemSceneLabel[];
  focusables: Focusable[];
  spheres: CollidableSphere[];
  /** Asteroid fields, with what it takes to hit one. See `asteroidHit`. */
  fields: CollidableField[];
  /**
   * Things that cook a hull from a distance.
   *
   * Separate from `spheres` because being *near* one is the danger, and most of
   * what you can hit is merely cold rock. See `radiantHeat`.
   */
  hotBodies: { node: THREE.Object3D; radius: number; kind: HeatSource }[];
  animate: (seconds: number) => void;
  dispose: () => void;
}

const PAD_COLORS: Record<string, number> = {
  small: 0x6f8fae,
  medium: 0x7fb46f,
  large: 0xd9a441,
  massive: 0xd9605f,
};

function disposeGroup(group: THREE.Group): void {
  group.traverse((object) => {
    const mesh = object as Partial<THREE.Mesh>;
    // The shared unit box outlives any one scene — disposing it here would
    // leave the next system's props pointing at a freed buffer.
    if (mesh.geometry && mesh.geometry !== unitBox) mesh.geometry.dispose();
    const material = mesh.material;
    if (Array.isArray(material)) material.forEach((m) => m.dispose());
    else material?.dispose();
  });
}

/**
 * How far a landing plate stands proud of the deck, as a fraction of the pad's
 * own width.
 *
 * ── Why the pad and not the deck ────────────────────────────────────────────
 *
 * It used to be a fraction of *deck thickness*, and deck thickness is rolled
 * per station at between a twentieth and a ninth of the deck's short side. The
 * result was plates from 0.7 to 18.6 world units tall across the galaxy — the
 * same class of berth, wildly different steps up, for a reason that had nothing
 * to do with the berth. Pad extents are quantised (25 world units for a small,
 * 100 for a massive), so measuring the plate against the pad makes a plate look
 * like a plate everywhere and still scale with the class of berth it is.
 *
 * Capped against the deck below, because a plate standing taller than the
 * structure carrying it reads as a mistake however consistent it is.
 */
const PAD_RELIEF = 0.06;

/**
 * How thick a landing plate is, in deck-local units.
 *
 * One function because three things need to agree about it: the mesh, the
 * capture volume's floor, and where a docked ship is parked. They disagreed
 * once and the ship sank into the plate.
 */
export function padPlateThickness(
  geometry: { thickness: number },
  pad: { extent: number },
): number {
  return Math.min(pad.extent * PAD_RELIEF, geometry.thickness * 0.5);
}

/**
 * The height of the surface a ship actually rests on, in deck-local units.
 *
 * The top of the plate: half the deck (its top face) plus the plate standing on
 * it. `dockVolume` builds its box up from here, and both views stand the hull
 * on it — so this is the single answer to "where is the ground".
 */
export function padSurface(
  geometry: { thickness: number },
  pad: { extent: number },
): number {
  return geometry.thickness / 2 + padPlateThickness(geometry, pad);
}

/** Flat text drawn to a canvas, for pad markings. */
function padMarkingTexture(label: string): THREE.CanvasTexture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.font = `bold ${size * 0.55}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, size / 2, size / 2);
  }
  return new THREE.CanvasTexture(canvas);
}

/**
 * One unit box, edge-tagged, shared by every prop in every station.
 *
 * Built lazily and never disposed: it is a handful of vertices, it is needed
 * for as long as anything is on screen, and freeing it per station would mean
 * recomputing the crease mask for every deck.
 */
let unitBox: THREE.BufferGeometry | null = null;
function propBox(): THREE.BufferGeometry {
  unitBox ??= withEdges(new THREE.BoxGeometry(1, 1, 1));
  return unitBox;
}

/**
 * A station deck with everything on it.
 *
 * Pads are inset slightly into the deck and coloured by class, with the class
 * letter painted on: which berths a station has should be readable from above
 * without opening a panel.
 *
 * Everything structural wears the edge shader, which is what makes a deck read
 * as a built thing at range rather than as a grey smudge. The props go in as a
 * single instanced draw — a large station carries the better part of a thousand
 * of them, and one mesh each was a thousand draw calls per station.
 */
export function buildStationDeck(geometry: StationGeometry, allegiance: string): THREE.Group {
  const group = new THREE.Group();

  const shape = new THREE.Shape(geometry.outline.map((p) => new THREE.Vector2(p.x, p.y)));
  const deckGeometry = new THREE.ExtrudeGeometry(shape, {
    depth: geometry.thickness,
    bevelEnabled: false,
  });
  deckGeometry.translate(0, 0, -geometry.thickness / 2);

  const hull = ALLEGIANCE_COLORS[allegiance as keyof typeof ALLEGIANCE_COLORS] ?? 0x8b93a3;
  // Tied to the deck's own size, so the edges firm up as a station recedes
  // whatever size it is. Measured in view distance, which is post-scale, so the
  // game scale has to be in here — a fade in generator units would put every
  // deck permanently at full strength.
  const fadeDistance = Math.max(120, geometry.radius * 90) * GAME_SCALE;
  const line = (color: THREE.ColorRepresentation) =>
    createStructureMaterial({ color, fadeDistance });

  group.add(new THREE.Mesh(withEdges(deckGeometry), line(hull)));

  const top = geometry.thickness / 2;

  for (const pad of geometry.pads) {
    const plateThickness = padPlateThickness(geometry, pad);

    const plate = new THREE.Mesh(
      withEdges(new THREE.BoxGeometry(pad.extent, pad.extent, plateThickness)),
      line(PAD_COLORS[pad.size] ?? 0x6f8fae),
    );
    // Standing *on* the deck, not straddling it. Centred on the deck's top face
    // the plate was half buried and half sticking up — and the half sticking up
    // was inside the capture volume, whose floor is that same face. It ate a
    // sixth of the height you had to land in and clipped through the hologram.
    plate.position.set(pad.position.x, pad.position.y, top + plateThickness / 2);
    group.add(plate);

    // Class letter, laid flat on the pad. Additive, because the deck under it
    // is now unlit black and an alpha-blended white would grey out rather than
    // glow — and the marking should read like the painted lines around it.
    const marking = new THREE.Mesh(
      new THREE.PlaneGeometry(pad.extent * 0.5, pad.extent * 0.5),
      new THREE.MeshBasicMaterial({
        map: padMarkingTexture(PAD_LABELS[pad.size]),
        color: PAD_COLORS[pad.size] ?? 0x6f8fae,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    marking.position.set(pad.position.x, pad.position.y, top + plateThickness * 1.02);
    group.add(marking);
  }

  if (geometry.props.length > 0) {
    const clutter = new THREE.InstancedMesh(
      propBox(),
      line(0x9aa4b2),
      geometry.props.length,
    );
    const transform = new THREE.Matrix4();
    const rotation = new THREE.Quaternion();
    const axis = new THREE.Vector3(0, 0, 1);

    geometry.props.forEach((prop, index) => {
      rotation.setFromAxisAngle(axis, prop.rotation);
      transform.compose(
        new THREE.Vector3(prop.position.x, prop.position.y, top + prop.size.z / 2),
        rotation,
        new THREE.Vector3(prop.size.x, prop.size.y, prop.size.z),
      );
      clutter.setMatrixAt(index, transform);
    });
    clutter.instanceMatrix.needsUpdate = true;
    group.add(clutter);
  }

  return group;
}

export function buildSystemScene(seed: string, systemId: SystemId): SystemScene {
  const system = getSystem(seed, systemId);
  const group = new THREE.Group();
  const labels: SystemSceneLabel[] = [];
  const focusables: Focusable[] = [];
  const stationNodes = new Map<string, THREE.Object3D>();
  const stationHulls: { deck: THREE.Object3D; radius: number; volumes: StationVolume[] }[] = [];
  const berths: Berth[] = [];
  const spheres: CollidableSphere[] = [];
  /** Things that cook a hull from a distance — see `radiantHeat`. */
  const hotBodies: { node: THREE.Object3D; radius: number; kind: HeatSource }[] = [];

  const moving: { node: THREE.Object3D; orbit: Orbit }[] = [];
  /** Bodies that turn on their own axis, and how fast. */
  const spinning: { node: THREE.Object3D; rate: number }[] = [];
  /** Whole asteroid fields, carried round their star as one. See `animate`. */
  const orbiting: { node: THREE.Object3D; rate: number; axis: THREE.Vector3 }[] = [];
  const fields: CollidableField[] = [];
  const materials: THREE.ShaderMaterial[] = [];
  let extent = system.starRadius * 4;

  const track = (node: THREE.Object3D, orbit: Orbit, parent: THREE.Object3D = group) => {
    parent.add(node);
    moving.push({ node, orbit });
  };

  const drawOrbit = (orbit: Orbit, parent: THREE.Object3D, opacity: number) => {
    parent.add(
      new THREE.LineLoop(
        new THREE.BufferGeometry().setFromPoints(
          orbitPath(orbit, 160).map((p) => new THREE.Vector3(p.x, p.y, p.z)),
        ),
        new THREE.LineBasicMaterial({ color: 0x6f8fae, transparent: true, opacity }),
      ),
    );
  };

  // ── Star ───────────────────────────────────────────────────────────────────
  const sunMaterial = createSunMaterial({
    color: system.starColor,
    seed: hashSeed(system.id),
    // In-system is the close view, so this is where the extra octaves earn out.
    quality: 'high',
  });
  materials.push(sunMaterial);
  const star = new THREE.Mesh(new THREE.SphereGeometry(system.starRadius, 64, 48), sunMaterial);
  group.add(star);
  spheres.push({ node: star, label: system.name, radius: system.starRadius });

  /*
   * The star is a body like any other.
   *
   * It was in `spheres`, so you could crash into it, and in nothing else — so it
   * had no mass lock and could not be targeted, and the flight console's "point
   * me at the star" fallback looked it up here by name and never found it. A sun
   * you can fly through at warp while the console shrugs is not a sun.
   */
  focusables.push({ node: star, label: system.name, radius: system.starRadius, kind: 'star' });
  hotBodies.push({ node: star, radius: system.starRadius, kind: 'star' });
  group.add(
    new THREE.Mesh(
      new THREE.SphereGeometry(system.starRadius * 2.2, 32, 24),
      createSunGlowMaterial(system.starColor),
    ),
  );

  const lightPosition = new THREE.Vector3(0, 0, 0);
  const planetPivots = new Map<string, THREE.Group>();

  /**
   * Give a body its own rotation.
   *
   * Seeded off the body rather than randomised, so a world turns the same way at
   * the same rate every time you visit it — and about the mesh's own Y, which is
   * where a `SphereGeometry`'s poles are and therefore where the planet shader
   * puts its caps. Spinning about any other axis would drag the ice caps round
   * the equator.
   */
  const spin = (node: THREE.Object3D, bodyId: string): void => {
    const random = Rng.forEntity(system.id, bodyId, 'spin');
    const period = SPIN_PERIOD.min + random.float() * (SPIN_PERIOD.max - SPIN_PERIOD.min);
    // A few turn the other way, as they do.
    const direction = random.float() < 0.15 ? -1 : 1;
    spinning.push({ node, rate: ((Math.PI * 2) / period) * direction });
  };

  // ── Planets and moons ──────────────────────────────────────────────────────
  for (const planet of system.planets) {
    drawOrbit(planet.orbit, group, 0.2);
    extent = Math.max(extent, planet.orbit.semiMajorAxis);

    const pivot = new THREE.Group();
    planetPivots.set(planet.id, pivot);
    track(pivot, planet.orbit);

    const material = createPlanetMaterial({
      type: planet.type,
      baseColor: planet.baseColor,
      accentColor: planet.accentColor,
      highlightColor: planet.highlightColor,
      seed: planet.shaderSeed,
      capExtent: planet.capExtent,
      lightPosition,
      lightColor: system.starColor,
      quality: 'high',
    });
    materials.push(material);

    const body = detailedSphere(planet.radius, material, {
      near: [96, 64],
      far: [32, 24],
    });
    pivot.add(body);
    spin(body, planet.id);
    spheres.push({ node: pivot, label: planet.name, radius: planet.radius });
    if (planet.type === 'volcanic') {
      hotBodies.push({ node: pivot, radius: planet.radius, kind: 'volcanic' });
    }

    labels.push({ text: planet.name, kind: 'planet', node: pivot, radius: planet.radius });
    focusables.push({ node: pivot, label: planet.name, radius: planet.radius, kind: 'planet' });

    for (const moon of planet.moons) {
      drawOrbit(moon.orbit, pivot, 0.13);

      // Moons run through the planet shader like everything else. They were flat
      // lit spheres surrounded by worlds with real surfaces, which made them
      // read as unfinished geometry rather than as small drab bodies.
      const moonMaterial = createPlanetMaterial({
        type: moon.type,
        baseColor: moon.baseColor,
        accentColor: moon.accentColor,
        highlightColor: moon.highlightColor,
        seed: moon.shaderSeed,
        capExtent: moon.capExtent,
        lightPosition,
        lightColor: system.starColor,
        // A moon is never the thing you are looking at for long, and there can
        // be three per planet, so they take the cheaper path.
        quality: 'low',
      });
      materials.push(moonMaterial);

      const moonMesh = detailedSphere(moon.radius, moonMaterial, {
        near: [64, 44],
        far: [20, 14],
      });
      track(moonMesh, moon.orbit, pivot);
      spin(moonMesh, moon.id);
      spheres.push({ node: moonMesh, label: moon.name, radius: moon.radius });
      if (moon.type === 'volcanic') {
        hotBodies.push({ node: moonMesh, radius: moon.radius, kind: 'volcanic' });
      }
      labels.push({ text: moon.name, kind: 'moon', node: moonMesh, radius: moon.radius });
      focusables.push({ node: moonMesh, label: moon.name, radius: moon.radius, kind: 'moon' });
    }
  }

  const frameFor = (parent: string | null): THREE.Object3D =>
    (parent && planetPivots.get(parent)) || group;

  // ── Stations, at full detail ───────────────────────────────────────────────
  for (const station of system.stations) {
    const frame = frameFor(station.parent);
    drawOrbit(station.orbit, frame, 0.12);
    extent = Math.max(extent, station.orbit.semiMajorAxis);

    const geometry = getStationGeometry(seed, station);
    const deck = buildStationDeck(geometry, station.allegiance ?? 'unaligned');
    deck.rotation.z = station.orbit.argumentOfPeriapsis;
    track(deck, station.orbit, frame);

    const pads = geometry.pads.length;
    labels.push({
      text: `${station.name} · ${pads} pad${pads === 1 ? '' : 's'} · ${geometry.shape}`,
      kind: 'station',
      node: deck,
      radius: geometry.radius,
    });
    focusables.push({
      node: deck,
      label: station.name,
      radius: geometry.radius,
      kind: 'station',
    });
    stationNodes.set(station.id, deck);
    stationHulls.push({ deck, radius: geometry.radius, volumes: stationVolumes(geometry) });

    for (const pad of geometry.pads) {
      berths.push({
        stationId: station.id,
        stationName: station.name,
        padId: pad.padId,
        size: pad.size,
        deck,
        // The surface you actually set down on, which is the top of the plate
        // rather than the deck it stands on. `dockVolume` builds up from here.
        local: new THREE.Vector3(pad.position.x, pad.position.y, padSurface(geometry, pad)),
        extent: pad.extent,
      });
    }
  }

  // ── Debris and anomalies ───────────────────────────────────────────────────
  for (const field of system.asteroidFields) {
    const frame = frameFor(field.parent);

    /*
     * Rocks, not rings.
     *
     * A field used to be four to six faint `LineLoop`s — a diagram of where the
     * asteroids would be, with nothing in it to fly between, point at or mine.
     * The rocks come from the seed, so every client draws the same field without
     * anyone sending it and a rock keeps its identity between sessions.
     *
     * One `InstancedMesh` per field: a few hundred rocks is one draw call and
     * one matrix upload, where a mesh each would be a few hundred of both.
     */
    /*
     * The rings, still drawn.
     *
     * The rocks are small and sparse on purpose — you fly between them — which
     * makes a field almost invisible from outside it. The bands are what say
     * "there is something here" from across a system, and what a pilot lines up
     * on before any individual rock is resolvable.
     */
    const bands = field.kind === 'belt' ? 6 : 4;

    for (let i = 0; i < bands; i++) {
      const offset = (i / (bands - 1) - 0.5) * field.radius;
      frame.add(
        new THREE.LineLoop(
          new THREE.BufferGeometry().setFromPoints(
            orbitPath({ ...field.orbit, semiMajorAxis: field.orbit.semiMajorAxis + offset }, 128)
              .map((p) => new THREE.Vector3(p.x, p.y, p.z)),
          ),
          new THREE.LineBasicMaterial({ color: 0xb0a08a, transparent: true, opacity: 0.35 }),
        ),
      );
    }

    const rocks = asteroidsIn(seed, field);

    /*
     * The ring's own normal, read off the curve.
     *
     * Two points ninety degrees apart, crossed. Taken from the curve rather than
     * rebuilt from inclination and node so it cannot disagree with whatever
     * convention `positionAt` uses — the axis is a fact about the ring, and this
     * asks the ring.
     */
    /*
     * Anomaly zero, and a quarter turn on — both stated explicitly.
     *
     * `positionAt(field.orbit, 0)` is *not* anomaly zero: it is wherever the
     * field's own `trueAnomaly` puts it. The rocks are laid out from zero, so
     * using it as the ring's origin rotated the whole slot lookup by the field's
     * position in its orbit — usually most of a belt — and the collision search
     * looked at rocks nowhere near the ship. Nothing collided, and nothing said
     * why.
     */
    const ringA = positionAt({ ...field.orbit, trueAnomaly: 0 }, 0);
    const ringB = positionAt({ ...field.orbit, trueAnomaly: Math.PI / 2 }, 0);
    const normal = new THREE.Vector3(ringA.x, ringA.y, ringA.z)
      .cross(new THREE.Vector3(ringB.x, ringB.y, ringB.z))
      .normalize();

    const swarmFrame = new THREE.Group();
    frame.add(swarmFrame);

    /*
     * Where every rock is, kept for the collision test.
     *
     * Written in the same pass that places the instances, so the shape you can
     * see and the shape you can hit cannot drift apart — the same rule the
     * ship's own hull and footprint already follow.
     */
    const centres = new Float32Array(rocks.length * 3);
    const placement = new THREE.Object3D();

    rocks.forEach((rock, at) => {
      const on = positionAt(rock.orbit, 0);
      // Off the plane along the ring's own normal: thickness everywhere, rather
      // than the twice-a-lap pinch a tilted orbit gives.
      placement.position.set(on.x, on.y, on.z).addScaledVector(normal, rock.height);

      centres[at * 3] = placement.position.x;
      centres[at * 3 + 1] = placement.position.y;
      centres[at * 3 + 2] = placement.position.z;
    });

    /*
     * The rocks.
     *
     * Two models, chosen by size — a boulder and a pebble are not the same
     * object made larger. Each is still scaled to its own radius, which is also
     * the radius the collision test uses, so what you can see and what you can
     * hit are the same number.
     *
     * Loaded asynchronously, so this fills in a frame or two after the field
     * appears. Nothing waits on it: the placeholder is already drawn, and a
     * field that pops into detail is better than a system that stalls on a
     * download.
     */
    const shaped = (model: string, wanted: (rock: (typeof rocks)[number]) => boolean) => {
      const chosen = rocks.filter(wanted);
      if (chosen.length === 0) return;

      void loadComponentGeometry(model).then((geometry) => {
        // The scene may have been torn down while this was in flight — a jump
        // is quite long enough to load an OBJ.
        if (!geometry || gone) return;

        const carved = new THREE.InstancedMesh(
          geometry,
          createStructureMaterial({
            color: new THREE.Color(fieldTint(seed, field.id)).getHex(),
            fadeDistance: 9000 * GAME_SCALE,
            edgeWorldWidth: 0.0175,
            tintSpread: 0.16,
          }),
          chosen.length,
        );

        const spot = new THREE.Object3D();
        chosen.forEach((rock, at) => {
          const on = positionAt(rock.orbit, 0);
          spot.position.set(on.x, on.y, on.z).addScaledVector(normal, rock.height);
          spot.rotation.set(rock.spin.x, rock.spin.y, rock.spin.z);
          spot.scale.setScalar(rock.radius);
          spot.updateMatrix();
          carved.setMatrixAt(at, spot.matrix);
        });

        carved.instanceMatrix.needsUpdate = true;
        swarmFrame.add(carved);
      });
    };

    /*
     * Which model a rock gets, by how big it is.
     *
     * A rock is scaled to its own radius either way, so this is about *shape*
     * rather than size: a boulder and a pebble are not the same object made
     * larger. The split is the midpoint of the size range.
     */
    const midpoint = MAX_ROCK_RADIUS / 2;
    shaped('asteroid_small', (rock) => rock.radius < midpoint);
    shaped('asteroid_large', (rock) => rock.radius >= midpoint);

    orbiting.push({ node: swarmFrame, rate: fieldSpin(field), axis: normal });

    /*
     * The ring's axes, as the collision test wants them.
     *
     * `along` is where the generator counts anomaly zero from and `across` is a
     * quarter turn on — the same two points the normal was crossed from, so
     * there is one derivation of the plane rather than two that could disagree.
     */
    fields.push({
      field,
      rocks,
      positions: centres,
      basis: {
        along: new THREE.Vector3(ringA.x, ringA.y, ringA.z).normalize(),
        across: new THREE.Vector3(ringB.x, ringB.y, ringB.z).normalize(),
        normal,
      },
      node: swarmFrame,
    });

    extent = Math.max(extent, field.orbit.semiMajorAxis);
  }

  for (const anomaly of system.anomalies) {
    drawOrbit(anomaly.orbit, group, 0.1);
    extent = Math.max(extent, anomaly.orbit.semiMajorAxis);

    // Anomalies are built things too, so they wear the same edge shader — which
    // is what stops a lone marker in open space reading as a flat purple dot.
    const marker = new THREE.Mesh(
      withEdges(new THREE.OctahedronGeometry(60)),
      createStructureMaterial({ color: 0xc98bff, fadeDistance: 6000 * GAME_SCALE }),
    );
    track(marker, anomaly.orbit, group);
    labels.push({ text: anomaly.name, kind: 'anomaly', node: marker, radius: 60 });
    focusables.push({ node: marker, label: anomaly.name, radius: 60, kind: 'anomaly' });
  }

  /**
   * Where the shader clock starts.
   *
   * Orbits run on absolute time — that is the whole point of a wall-clock world,
   * and it is exact because it stays in double precision on the CPU. Shader time
   * does not: `uTime` is a `float` uniform, and by now the wall clock has driven
   * it past a hundred thousand, where a float32 cannot resolve better than four
   * milliseconds of shader time. At this world's pace that is ninety-odd frames
   * during which the value literally cannot change, so every animated surface —
   * cloud bands, the star — advanced in visible jumps rather than moving.
   *
   * Rebasing to the moment the scene was built keeps it near zero. A constant
   * phase offset is invisible: nothing about a star's surface depends on which
   * second of the epoch it started from.
   */
  const epoch = flightTime();

  /*
   * Whether this scene has been torn down.
   *
   * Asteroid models load asynchronously, and a jump is quite long enough for one
   * to arrive after the system it belongs to has gone. Adding to a disposed
   * scene leaks the mesh and draws it over the next system.
   */
  let gone = false;

  const animate = (seconds: number): void => {
    for (const { node, orbit } of moving) {
      const at = positionAt(orbit, seconds);
      node.position.set(at.x, at.y, at.z);
    }
    for (const { node, rate } of spinning) node.rotation.y = seconds * rate;
    /*
     * Belts turn about the orbital plane's normal, not their own axis.
     *
     * `spinning` is for bodies rotating on their own — a planet's day — and
     * turns about y. A field's rocks are laid out in the xy-plane, so carrying
     * them round the star is a rotation about z. Same shape of list, different
     * axis, and mixing them would make every belt tumble sideways.
     */
    for (const { node, rate, axis } of orbiting) {
      node.setRotationFromAxisAngle(axis, seconds * rate);
    }
    for (const material of materials) material.uniforms['uTime']!.value = seconds - epoch;
  };
  animate(0);

  // Everything above is built in generator units and blown up as a whole, so
  // orbits, bodies and decks all keep their proportions exactly and there is
  // one place the scale is applied rather than a factor sprinkled through every
  // construction site.
  group.scale.setScalar(GAME_SCALE);

  // What callers are told has to be in the same units as what they see, or the
  // camera frames the wrong volume and every marker is sized for the old world.
  const scaled = (label: SystemSceneLabel) => ({ ...label, radius: label.radius * GAME_SCALE });

  return {
    group,
    extent: extent * GAME_SCALE,
    stationNodes,
    stationHulls,
    berths,
    lightPosition,
    labels: labels.map(scaled),
    focusables: focusables.map((f) => ({ ...f, radius: f.radius * GAME_SCALE })),
    spheres: spheres.map((s) => ({ ...s, radius: s.radius * GAME_SCALE })),
    // Not scaled: these are read in the field's own frame, which is inside the
    // group the scale is applied to. Everything the test compares — positions,
    // radii, the ship once converted — is in generator units together.
    fields,
    hotBodies: hotBodies.map((b) => ({ ...b, radius: b.radius * GAME_SCALE })),
    animate,
    dispose: () => disposeGroup(group),
  };
}
